import { ReactNode } from 'react'
import {
  IconBook2,
  IconCategory,
  IconChartBar,
  IconCode,
  IconDatabase,
  IconDeviceMobile,
  IconEdit,
  IconFileDescription,
  IconHome,
  IconLayoutDashboard,
  IconPhoto,
  IconTerminal2,
  IconUsers,
} from '@common/ui/icons'
import { com } from '@eidu/web'
import { KnownKindKey, getKnownKindKey } from '../../domain/kindRegistry'
import { ViewConfig } from './kinds/KindPages'
import DefaultViewPage from './kinds/default/DefaultViewPage'
import DefaultEditPage from './kinds/default/DefaultEditPage'
import UserViewPage from './kinds/user/UserViewPage'
import UserEditPage from './kinds/user/UserEditPage'
import LearningUnitViewPage from './kinds/learning-unit/LearningUnitViewPage'
import LearningUnitEditPage from './kinds/learning-unit/LearningUnitEditPage'
import FormAssetViewPage from './kinds/form/FormAssetViewPage'
import FormAssetEditPage from './kinds/form/FormAssetEditPage'
import AssetViewPage from './kinds/asset/AssetViewPage'
import AssetEditPage from './kinds/asset/AssetEditPage'
import RuntimeViewPage from './kinds/runtime/RuntimeViewPage'
import RuntimeEditPage from './kinds/runtime/RuntimeEditPage'
import GenericAssetViewPage from './kinds/generic-asset/GenericAssetViewPage'
import GenericAssetEditPage from './kinds/generic-asset/GenericAssetEditPage'
import EntityTypeKind = com.eidu.entity.type.EntityTypeKind
import { iconSizes } from '@common/ui'

type KindViewConfig = {
  icon: ReactNode
  viewConfig: ViewConfig
}

const defaultPageConfig: ViewConfig = { ViewPage: DefaultViewPage, EditPage: DefaultEditPage }
const assetPageConfig: ViewConfig = { ViewPage: AssetViewPage, EditPage: AssetEditPage }

const kindViewRegistry: Record<KnownKindKey, KindViewConfig> = {
  User: {
    icon: <IconUsers size={iconSizes.sm} stroke={1.5} />,
    viewConfig: { ViewPage: UserViewPage, EditPage: UserEditPage },
  },
  School: { icon: <IconHome size={iconSizes.sm} stroke={1.5} />, viewConfig: defaultPageConfig },
  Generic: { icon: <IconCategory size={iconSizes.sm} stroke={1.5} />, viewConfig: defaultPageConfig },
  LearningUnit: {
    icon: <IconBook2 size={iconSizes.sm} stroke={1.5} />,
    viewConfig: { ViewPage: LearningUnitViewPage, EditPage: LearningUnitEditPage },
  },
  Form: {
    icon: <IconFileDescription size={iconSizes.sm} stroke={1.5} />,
    viewConfig: { ViewPage: FormAssetViewPage, EditPage: FormAssetEditPage },
  },
  Dataset: { icon: <IconDatabase size={iconSizes.sm} stroke={1.5} />, viewConfig: assetPageConfig },
  Chart: { icon: <IconChartBar size={iconSizes.sm} stroke={1.5} />, viewConfig: assetPageConfig },
  Dashboard: { icon: <IconLayoutDashboard size={iconSizes.sm} stroke={1.5} />, viewConfig: assetPageConfig },
  Runtime: {
    icon: <IconTerminal2 size={iconSizes.sm} stroke={1.5} />,
    viewConfig: { ViewPage: RuntimeViewPage, EditPage: RuntimeEditPage },
  },
  Feature: { icon: <IconCode size={iconSizes.sm} stroke={1.5} />, viewConfig: assetPageConfig },
  Config: { icon: <IconDeviceMobile size={iconSizes.sm} stroke={1.5} />, viewConfig: assetPageConfig },
  Asset: {
    icon: <IconPhoto size={iconSizes.sm} stroke={1.5} />,
    viewConfig: { ViewPage: GenericAssetViewPage, EditPage: GenericAssetEditPage },
  },
}

export const iconForEntityTypeKind = (
  kind: EntityTypeKind,
  fallbackIcon: ReactNode = <IconEdit size={iconSizes.sm} stroke={1.5} />
): ReactNode => {
  const key = getKnownKindKey(kind)
  return key ? kindViewRegistry[key].icon : fallbackIcon
}

export const getKindPageConfig = (kind: EntityTypeKind): ViewConfig => {
  const key = getKnownKindKey(kind)
  return key ? kindViewRegistry[key].viewConfig : defaultPageConfig
}
